import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Button from '@/components/Button/Button'
import Decoration from '@/components/Decoration/Decoration'
import UIText from '@/components/UI/UIText'
import UITitle from '@/components/UI/UITitle'
import { useAppState } from '@/composables/useAppState'
import useAudioMusic from '@/composables/useAudioMusic'
import { useGSAP } from '@/composables/useGSAP'
import useTracking from '@/composables/useTracking'
import { reportError } from '@/instrumentation'
import AudioEngine, { getAudioPath, getSubtitlePath } from '@/webgl/audio/AudioEngine'

import IntroEnterSwirl from '../Interlude/IntroEnterSwirl'
import LoadingExitSwirl from '../Loading/LoadingExitSwirl'

type Mood = {
  path: string
}

export default function Ending() {
  const music = useAudioMusic()

  const { persona, scenario, scenarioUnitName, actor } = useAppState()

  const { t } = useTranslation()
  const { gsap } = useGSAP()

  const [abortController] = useState(new AbortController())

  const [bundle, setBundle] = useState(null)
  const [sound, setSound] = useState(null)

  const unmount = useCallback(() => {
    sound?.stop()
    sound?.onEnded.release()
    abortController?.abort()
  }, [sound, abortController])

  const [isLeaving, setIsLeaving] = useState(false)

  const onContinue = useCallback(() => {
    unmount()

    const ctx = gsap.context(() => {
      gsap
        .timeline()
        .add(() => setIsLeaving(true), 0)
        .add(() => actor.send({ type: 'NEXT' }), 1.55)
    })

    return () => ctx.kill()
  }, [actor, gsap, setIsLeaving, unmount])

  const nextClick = useCallback(() => {
    abortController?.abort()
    if (sound) sound?.stop()
    else onContinue()
  }, [sound, onContinue, abortController])

  const loadAudio = useCallback(() => {
    const bundle = AudioEngine.createBundle()
    setBundle(bundle)
    const audioFilePath = `${persona}_${scenario.current + 1}/audios/outro.mp3`
    const audioSrc = audioFilePath ? getAudioPath(audioFilePath) : null
    const audioSubtitlesPath = `${persona}_${scenario.current + 1}/audios/outro.en.vtt`
    const audioSubtitleSrc = getSubtitlePath(audioSubtitlesPath)
    bundle.addFile(audioSrc, audioSubtitleSrc)

    bundle
      .load(abortController.signal)
      .then(() => {
        const sound = AudioEngine.play('outro')
        setSound(sound)

        if (sound) {
          sound.onEnded.on(() => {
            bundle?.dispose()
            onContinue()
          }, true)
        } else {
          console.warn('[Ending] no sound')
        }
      })
      .catch((e) => reportError(e, { feature: 'ending-audio' }))
  }, [abortController, persona, scenario, onContinue])

  const mount = useCallback(() => {
    loadAudio()
  }, [loadAudio])

  useEffect(() => {
    let unmounting = false
    setTimeout(() => {
      if (unmounting) return
      mount()
    }, 0)

    return () => {
      unmounting = true
    }
    // eslint-disable-next-line
  }, [])

  const focusRef = useRef<HTMLDivElement>(null)
  useLayoutEffect(() => {
    // focusRef.current?.focus({ preventScroll: true });
  }, [focusRef])

  const { trackImpression, trackClick } = useTracking()
  useLayoutEffect(() => {
    trackImpression({
      unitName: `${persona}_simulation${scenarioUnitName}`,
      unitVariation: 'scenario_end',
      unitLocation: 'tyce',
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenarioUnitName])

  const onInnerContinue = useCallback(() => {
    trackClick({
      unitName: `${persona}_simulation${scenarioUnitName}`,
      unitVariation: 'scenario_end',
      unitLocation: 'tyce',

      clickType: 'button',
      buttonText: t(`ending.${persona}.${scenario.current + 1}.cta`),
      buttonName: 'navigation_button',
    })
    nextClick()
  }, [trackClick, nextClick, t, persona, scenario, scenarioUnitName])

  return (
    <div data-testid='ending' className='Ending col-span-1 col-start-1 row-span-1 w-full'>
      <Decoration show />

      <div
        className='absolute w-full h-full z-1 flex items-center justify-center transition-transform duration-1000 ease-out-quad'
        aria-hidden='true'
      >
        <div className={`relative transition-opacity duration-500 ease-out-quad ${isLeaving ? 'opacity-0' : ''}`}>
          <svg
            className='w-[42vw] h-[42vw] text-dark-cream'
            viewBox='0 0 40 40'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <circle cx='12.3284' cy='9.72475' r='4.22485' fill='currentColor' />
            <circle cx='27.5393' cy='9.72475' r='4.22485' fill='currentColor' />
            <path
              d='M3 35.2C3 26.1 10.5 18.3 20 18.3C29.5 18.3 37 26.1 37 35.2'
              stroke='currentColor'
              fill='none'
              strokeWidth='5'
            />
          </svg>
        </div>
      </div>

      <div
        className={`relative w-full h-full z-2 flex items-center transition-opacity duration-500 ease-out-quad ${isLeaving ? 'opacity-0' : ''}`}
      >
        <div
          ref={focusRef}
          className='Ending__Inner container content-center rounded-12 focus-visible:outline focus-visible:outline-2 outline-scarlet outline-offset-8'
          tabIndex={-1}
        >
          {/* We may like to bring hasSideDecoration back on desktop eventually */}
          <UITitle
            is='h1'
            i18nKey={`ending.${persona}.${scenario.current + 1}.title`}
            className='text-heading-h3 md:text-heading-h1 text-center col-start-1 col-span-10 lg:col-start-1 lg:col-span-12 mt-48 mb-48'
          />
          <UIText
            is='p'
            delay={0.85}
            className='text-body text-center col-start-1 col-span-10 lg:col-start-1 lg:col-span-12 mb-48'
            i18nKey={`ending.${persona}.${scenario.current + 1}.subtitle`}
          />
          <div className='flex justify-center items-center col-start-3 lg:col-start-5 col-span-6 lg:col-span-4'>
            <Button hasParticles={true} appear appearDelay={1} onClick={onInnerContinue}>
              {t(`ending.${persona}.${scenario.current + 1}.cta`)}
            </Button>
          </div>
        </div>
      </div>

      <IntroEnterSwirl />
      {isLeaving && <LoadingExitSwirl delay={0} />}
    </div>
  )
}
